import { Chip, Stack, Typography } from '@mui/material';

export const FLAG_DEFINITIONS = [
  { key: 'atomicity', label: 'Атомарность' },
  { key: 'unambiguous', label: 'Однозначность' },
  { key: 'testable', label: 'Проверяемость' },
  { key: 'complete', label: 'Полнота' },
];

export function normalizeRequirementValidation(requirement) {
  if (!requirement) {
    return null;
  }

  const block = requirement?.validation || null;

  const issueSources = [
    requirement?.issues,
    requirement?.validationIssues,
    block?.issues,
    requirement?.validationErrors,
  ];

  const issues = issueSources
    .flatMap((value) => {
      if (Array.isArray(value)) return value;
      if (typeof value === 'string') return [value];
      return [];
    })
    .filter((value) => typeof value === 'string')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);

  const pickBoolean = (key) => {
    const direct = requirement?.[key];
    if (typeof direct === 'boolean') return direct;

    const snake = requirement?.[key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)];
    if (typeof snake === 'boolean') return snake;

    const blockValue = block?.[key];
    if (typeof blockValue === 'boolean') return blockValue;

    const blockSnake = block?.[key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)];
    if (typeof blockSnake === 'boolean') return blockSnake;

    return null;
  };

  const suggestionCandidates = [
    requirement?.suggestedRewrite,
    requirement?.suggested_rewrite,
    block?.suggestedRewrite,
    block?.suggested_rewrite,
  ];

  const suggestedRewrite =
    suggestionCandidates
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .find((v) => v.length > 0) || null;

  const normalized = {
    atomicity: pickBoolean('atomicity'),
    unambiguous: pickBoolean('unambiguous'),
    testable: pickBoolean('testable'),
    complete: pickBoolean('complete'),
    issues,
    suggestedRewrite,
  };

  const hasFlags = FLAG_DEFINITIONS.some(({ key }) => typeof normalized[key] === 'boolean');
  const hasSuggestion = typeof normalized.suggestedRewrite === 'string' && normalized.suggestedRewrite.length > 0;

  if (!hasFlags && normalized.issues.length === 0 && !hasSuggestion) {
    return null;
  }

  return normalized;
}

export default function RequirementValidationBlock({ validation, showFlagsOnly = false, compact = false, }) {
  if (!validation) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  const issues = Array.isArray(validation.issues) ? validation.issues : [];
  const suggestedRewrite =
    typeof validation.suggestedRewrite === 'string' ? validation.suggestedRewrite : null;

  return (
    <Stack spacing={showFlagsOnly ? 1 : 1.5} sx={{ width: '100%', minWidth: 120 }}>
      
      {/* ВЕРТИКАЛЬНЫЙ СТОЛБИК ЧИПОВ */}
      <Stack
        direction="column"
        spacing={0.5}
        sx={{
          width: '100%',
        }}
      >
        {FLAG_DEFINITIONS.map(({ key, label }) => {
          const value = validation[key];

          const color =
            value === true ? 'success' :
            value === false ? 'error' :
            'default';

          return (
            <Chip
              key={key}
              size="small"
              color={color}
              variant="filled"
              label={label}
              sx={{
                width: compact ? "25%" : "100%",         
                "& .MuiChip-label": {
                  fontSize: compact ? "0.9rem" : "0.9rem",
                  px: compact ? 1 : 2,
                },
              }}
            />
          );
        })}
      </Stack>

      {!showFlagsOnly && issues.length > 0 && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">Проблемы:</Typography>
          {issues.map((issue, idx) => (
            <Typography
              key={idx}
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              • {issue}
            </Typography>
          ))}
        </Stack>
      )}

      {!showFlagsOnly && suggestedRewrite && (
        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            Предлагаемая формулировка:
          </Typography>
          <Typography
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {suggestedRewrite}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
